<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[100]">
      <!-- Dim backdrop -->
      <div class="absolute inset-0 bg-black/50 transition-opacity" @click="skip" />

      <!-- Spotlight ring over the target element -->
      <div
        v-if="ring"
        class="absolute rounded-xl border-2 border-mlb-gold shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] pointer-events-none transition-all duration-300"
        :style="{
          top: `${ring.top - 6}px`,
          left: `${ring.left - 6}px`,
          width: `${ring.width + 12}px`,
          height: `${ring.height + 12}px`,
          background: 'transparent',
        }"
      />

      <!-- Step card -->
      <div
        class="absolute bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 w-[340px] max-w-[calc(100vw-32px)] p-5 transition-all duration-300"
        :style="cardStyle"
      >
        <p class="text-[10px] font-bold uppercase tracking-widest text-mlb-gold mb-1" style="font-family: var(--font-display)">
          {{ index + 1 }} of {{ steps.length }}
        </p>
        <h3 class="text-base font-black text-gray-900 dark:text-white mb-1.5" style="font-family: var(--font-display)">
          {{ step.title }}
        </h3>
        <p class="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{{ step.body }}</p>

        <div class="flex items-center justify-between mt-4">
          <button @click="skip" class="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
            Skip tour
          </button>
          <div class="flex items-center gap-2">
            <button
              v-if="index > 0"
              @click="go(index - 1)"
              class="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300"
            >
              Back
            </button>
            <button
              @click="index === steps.length - 1 ? finish() : go(index + 1)"
              class="text-sm px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {{ index === steps.length - 1 ? 'Done' : 'Next' }}
            </button>
          </div>
        </div>

        <!-- Progress dots -->
        <div class="flex justify-center gap-1.5 mt-3">
          <span
            v-for="(s, i) in steps"
            :key="i"
            class="w-1.5 h-1.5 rounded-full"
            :class="i === index ? 'bg-mlb-gold' : 'bg-gray-300 dark:bg-slate-600'"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import type { TourStep } from '@/tours'

const props = defineProps<{ steps: TourStep[] }>()
const emit = defineEmits<{ (e: 'done'): void }>()

const router = useRouter()
const index = ref(0)
const ring = ref<{ top: number; left: number; width: number; height: number } | null>(null)

const step = computed(() => props.steps[index.value])

const cardStyle = computed(() => {
  if (!ring.value) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
  }
  const r = ring.value
  const below = r.top + r.height + 16
  // Place under the target when there's room; otherwise above.
  const top = below + 260 < window.innerHeight ? below : Math.max(16, r.top - 240)
  const left = Math.min(Math.max(16, r.left), window.innerWidth - 360)
  return { top: `${top}px`, left: `${left}px` }
})

async function locateTarget() {
  ring.value = null
  const s = step.value
  if (s.route && router.currentRoute.value.path !== s.route) {
    await router.push(s.route)
  }
  if (!s.target) return
  // The target may render after a route change / data load — retry briefly.
  for (let attempt = 0; attempt < 10; attempt++) {
    const el = document.querySelector(s.target)
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior })
      const rect = el.getBoundingClientRect()
      ring.value = { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      return
    }
    await new Promise(res => setTimeout(res, 120))
  }
}

function go(i: number) {
  index.value = Math.min(Math.max(i, 0), props.steps.length - 1)
  locateTarget()
}

function finish() {
  emit('done')
}

function skip() {
  emit('done')
}

function reposition() {
  if (step.value?.target) locateTarget()
}

onMounted(() => {
  locateTarget()
  window.addEventListener('resize', reposition)
})
onUnmounted(() => window.removeEventListener('resize', reposition))
</script>
